import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit, Trash2, GripVertical, ExternalLink,
  Settings, Save, X, Eye, EyeOff, Link, Folder
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Popular Lucide icons for tool selection
const ICON_OPTIONS = [
  'Wrench', 'Calculator', 'Calendar', 'Image', 'FileText', 'Folder',
  'BarChart', 'PieChart', 'TrendingUp', 'DollarSign', 'CreditCard',
  'ShoppingCart', 'Package', 'Truck', 'Users', 'UserCheck', 'Mail',
  'MessageSquare', 'Phone', 'Globe', 'Link', 'Share2', 'Download',
  'Upload', 'Cloud', 'Database', 'Server', 'Code', 'Terminal',
  'Palette', 'Brush', 'Pencil', 'Layout', 'Grid', 'List',
  'BookOpen', 'Newspaper', 'Video', 'Music', 'Camera', 'Mic',
  'Settings', 'Sliders', 'Filter', 'Search', 'Zap', 'Rocket'
];

const CATEGORIES = [
  'General', 'Design', 'Marketing', 'Finance', 'Analytics', 
  'Communication', 'Productivity', 'Development', 'Management'
];

const AdminTools = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [iconSearch, setIconSearch] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'Wrench',
    type: 'external',
    url: '',
    route: '',
    category: 'General',
    status: 'active',
    requiresAuth: true
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard');
      toast({
        title: "Access Denied",
        description: "Only Super Admins can manage tools",
        variant: "destructive"
      });
      return;
    }
    fetchTools();
  }, [user]);

  const fetchTools = async () => {
    try {
      const response = await axios.get(`${API}/tools`);
      setTools(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load tools",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTool(null);
    setFormData({
      name: '',
      description: '',
      icon: 'Wrench',
      type: 'external',
      url: '',
      route: '',
      category: 'General',
      status: 'active',
      requiresAuth: true
    });
    setModalOpen(true);
  };

  const openEditModal = (tool) => {
    setEditingTool(tool);
    setFormData({
      name: tool.name,
      description: tool.description,
      icon: tool.icon || 'Wrench',
      type: tool.type,
      url: tool.url || '',
      route: tool.route || '',
      category: tool.category || 'General',
      status: tool.status,
      requiresAuth: tool.requiresAuth !== false
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.description.trim()) {
      toast({
        title: "Validation Error",
        description: "Name and description are required",
        variant: "destructive"
      });
      return;
    }

    if (formData.type === 'external' && !formData.url.trim()) {
      toast({
        title: "Validation Error",
        description: "URL is required for external tools",
        variant: "destructive"
      });
      return;
    }

    if (formData.type === 'internal' && !formData.route.trim()) {
      toast({
        title: "Validation Error",
        description: "Route is required for internal tools",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      if (editingTool) {
        await axios.put(`${API}/tools/${editingTool._id}`, formData);
        toast({ title: "Tool Updated", description: `${formData.name} has been updated` });
      } else {
        await axios.post(`${API}/tools`, formData);
        toast({ title: "Tool Created", description: `${formData.name} has been created` });
      }
      setModalOpen(false);
      fetchTools();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to save tool",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tool) => {
    if (!window.confirm(`Are you sure you want to delete "${tool.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/tools/${tool._id}`);
      toast({ title: "Tool Deleted", description: `${tool.name} has been deleted` });
      fetchTools();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete tool",
        variant: "destructive"
      });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newTools = [...tools];
    const [draggedTool] = newTools.splice(draggedIndex, 1);
    newTools.splice(index, 0, draggedTool);
    setTools(newTools);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;
    
    // Save new order to backend
    const toolOrders = tools.map((tool, index) => ({
      id: tool._id,
      order: index
    }));

    try {
      await axios.put(`${API}/tools/reorder/bulk`, { toolOrders });
      toast({ title: "Order Saved", description: "Tool order has been updated" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save order",
        variant: "destructive"
      });
      fetchTools(); // Revert to original order
    }
    
    setDraggedIndex(null);
  };

  const getIconComponent = (iconName) => {
    const Icon = LucideIcons[iconName] || LucideIcons.Wrench;
    return Icon;
  };

  const filteredIcons = ICON_OPTIONS.filter(icon => 
    icon.toLowerCase().includes(iconSearch.toLowerCase())
  );

  if (!isSuperAdmin) return null;

  return (
    <div className="bg-black text-white pt-24 min-h-screen">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
            >
              <ArrowLeft size={18} className="mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Tool Manager</h1>
              <p className="text-gray-400">Create, edit, and organize dashboard tools</p>
            </div>
          </div>
          <Button
            onClick={openCreateModal}
            className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
            data-testid="create-tool-btn"
          >
            <Plus size={18} className="mr-2" />
            New Tool
          </Button>
        </div>

        {/* Tools List */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
            <p className="text-gray-400">Loading tools...</p>
          </div>
        ) : tools.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Settings className="mx-auto text-gray-500 mb-4" size={48} />
              <h3 className="text-xl font-semibold mb-2">No Tools Created</h3>
              <p className="text-gray-400 mb-4">Create your first tool to get started</p>
              <Button onClick={openCreateModal} className="bg-[#F4B400] text-black hover:bg-[#d49f00]">
                <Plus size={18} className="mr-2" />
                Create First Tool
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-400 mb-4">
              Drag and drop to reorder tools. Changes are saved automatically.
            </p>
            {tools.map((tool, index) => {
              const IconComponent = getIconComponent(tool.icon);
              return (
                <Card
                  key={tool._id}
                  className={`bg-white text-black hover:shadow-lg transition-all cursor-move ${
                    draggedIndex === index ? 'opacity-50 scale-[0.98]' : ''
                  }`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  data-testid={`tool-item-${tool.slug}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="cursor-grab active:cursor-grabbing">
                        <GripVertical className="text-gray-400" size={20} />
                      </div>
                      
                      <div className="bg-[#F4B400] w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-black" size={24} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold truncate">{tool.name}</h3>
                          <Badge className={`text-xs ${
                            tool.status === 'active' ? 'bg-green-500' :
                            tool.status === 'coming_soon' ? 'bg-yellow-500' :
                            'bg-gray-500'
                          } text-white`}>
                            {tool.status === 'active' ? 'Active' : 
                             tool.status === 'coming_soon' ? 'Coming Soon' : 'Disabled'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {tool.type === 'external' ? 'External' : 'Internal'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {tool.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{tool.description}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                          {tool.type === 'external' ? (
                            <>
                              <Link size={12} />
                              <span className="truncate">{tool.url}</span>
                            </>
                          ) : (
                            <>
                              <Folder size={12} />
                              <span>{tool.route}</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {tool.type === 'external' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(tool.url, '_blank')}
                          >
                            <ExternalLink size={16} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditModal(tool)}
                          data-testid={`edit-tool-${tool.slug}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(tool)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-tool-${tool.slug}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Tool Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white text-black max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingTool ? <Edit size={20} /> : <Plus size={20} />}
              {editingTool ? 'Edit Tool' : 'Create New Tool'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Name & Icon Row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label htmlFor="tool-name">Tool Name *</Label>
                <Input
                  id="tool-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., DTF Calculator"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Icon</Label>
                <Select 
                  value={formData.icon} 
                  onValueChange={(value) => setFormData({ ...formData, icon: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        {React.createElement(getIconComponent(formData.icon), { size: 16 })}
                        <span>{formData.icon}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <div className="p-2">
                      <Input
                        placeholder="Search icons..."
                        value={iconSearch}
                        onChange={(e) => setIconSearch(e.target.value)}
                        className="mb-2"
                      />
                    </div>
                    {filteredIcons.map((iconName) => {
                      const Icon = getIconComponent(iconName);
                      return (
                        <SelectItem key={iconName} value={iconName}>
                          <div className="flex items-center gap-2">
                            <Icon size={16} />
                            <span>{iconName}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="tool-desc">Description *</Label>
              <Textarea
                id="tool-desc"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of what this tool does"
                className="mt-2"
                rows={2}
              />
            </div>

            {/* Type & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tool Type *</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">
                      <div className="flex items-center gap-2">
                        <ExternalLink size={14} />
                        External URL
                      </div>
                    </SelectItem>
                    <SelectItem value="internal">
                      <div className="flex items-center gap-2">
                        <Folder size={14} />
                        Internal Route
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* URL or Route based on type */}
            {formData.type === 'external' ? (
              <div>
                <Label htmlFor="tool-url">External URL *</Label>
                <Input
                  id="tool-url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://example.com/tool"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Full URL where the tool is hosted (opens in new tab)
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="tool-route">Internal Route *</Label>
                <Input
                  id="tool-route"
                  value={formData.route}
                  onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                  placeholder="/dashboard/admin/portfolio"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Internal route path (e.g., /dashboard/admin/blog)
                </p>
              </div>
            )}

            {/* Status & Auth */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      <div className="flex items-center gap-2">
                        <Eye size={14} className="text-green-500" />
                        Active
                      </div>
                    </SelectItem>
                    <SelectItem value="coming_soon">
                      <div className="flex items-center gap-2">
                        <EyeOff size={14} className="text-yellow-500" />
                        Coming Soon
                      </div>
                    </SelectItem>
                    <SelectItem value="disabled">
                      <div className="flex items-center gap-2">
                        <X size={14} className="text-red-500" />
                        Disabled
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-8">
                <Switch
                  id="requires-auth"
                  checked={formData.requiresAuth}
                  onCheckedChange={(checked) => setFormData({ ...formData, requiresAuth: checked })}
                />
                <Label htmlFor="requires-auth" className="cursor-pointer">
                  Requires Authentication
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
            >
              {saving ? 'Saving...' : (
                <>
                  <Save size={16} className="mr-2" />
                  {editingTool ? 'Update Tool' : 'Create Tool'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTools;
